import { useEffect, useState, useRef } from 'react';

import { useNavigatorOnLine } from '@treezor/design-system';
import jwt_decode from 'jwt-decode';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { useCurrentSupportUser } from '@/features/dashboard-users/hooks/useCurrentSupportUser';

import { openAlert, closeAlert } from '@/store/actions/alert.actions';

import { advancedRoleStatus } from '@/features/roles/store/models/advancedRole.model';

import { TAlertKey } from '@/store/types/alert.types';
import { IAccessTokenDecoded } from '@/store/models/auth.model';
import { useExpirationStatus } from '@/features/dashboard-users/hooks/useexpirationStatus';

export const timer = 5 * 60 * 1000;

export function useAlerts() {
  const dispatch = useAppDispatch();
  const alertState = useAppSelector((state) => state.alert);
  const currentSupportUser = useCurrentSupportUser();

  const { isNearExpiration } = useExpirationStatus(currentSupportUser);

  const alerts: TAlertKey[] = Object.entries(alertState).reduce((acc, [key, value]) => {
    if (value.isDisplayed && !value.isValidated) {
      acc = [key, ...acc]; // last alert on top of the list
    }
    return acc;
  }, []);

  // offlineAlert
  const isClientIsOnline = useNavigatorOnLine();
  useEffect(() => {
    if (!isClientIsOnline) {
      dispatch(openAlert('offlineWarning'));
    } else {
      alerts.includes('offlineWarning') && dispatch(closeAlert('offlineWarning'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientIsOnline]);

  // logoutAlert
  const [shouldDisplayReminder, setShouldDisplayReminder] = useState<boolean>(false);
  const [shouldDisplayWarning, setShouldDisplayWarning] = useState<boolean>(false);
  const timerInterval = useRef<NodeJS.Timeout | undefined>(undefined);

  const accessToken = useAppSelector((state) => state.auth?.token?.access_token);
  const refreshToken = useAppSelector((state) => state.auth?.token?.refresh_token);

  const decodedToken = accessToken ? jwt_decode<IAccessTokenDecoded>(accessToken) : undefined;
  const tokenExpirationDate = decodedToken.exp && decodedToken.exp * 1000;

  useEffect(() => {
    const tick = () => {
      if (tokenExpirationDate && tokenExpirationDate < Date.now()) {
        setShouldDisplayWarning(true);
      } else {
        setShouldDisplayWarning(false);
        if (refreshToken && tokenExpirationDate && tokenExpirationDate < Date.now() + timer) {
          setShouldDisplayReminder(true);
        } else {
          setShouldDisplayReminder(false);
        }
      }
    };
    timerInterval.current = setInterval(() => tick(), 5000);
    return () => {
      clearInterval(timerInterval.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenExpirationDate]);

  useEffect(() => {
    if (shouldDisplayWarning) {
      if (!alerts.includes('logoutWarning')) {
        dispatch(openAlert('logoutWarning'));
      }
      alerts.includes('logoutReminder') && dispatch(closeAlert('logoutReminder'));
    } else {
      if (shouldDisplayReminder) {
        !alerts.includes('logoutReminder') && dispatch(openAlert('logoutReminder'));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldDisplayWarning, shouldDisplayReminder]);

  //scopeAlert
  const hasAdvancedRole = currentSupportUser?.metadata?.role?.status === advancedRoleStatus.VALIDATED;

  const scopes =
    currentSupportUser.clients
      ?.map((client) => client.scopes)
      .reduce((allScopes, scopes) => [...allScopes, ...scopes], []) || [];

  const shouldShowScopeAlert =
    !hasAdvancedRole &&
    (scopes.includes('support_user_management') ||
      (scopes.includes('read_write') && !scopes.includes('support_user_management')) ||
      !scopes.includes('read_write'));

  useEffect(() => {
    if (shouldShowScopeAlert) {
      dispatch(openAlert('userScopesWarning'));
    }
  }, [dispatch, shouldShowScopeAlert]);

  //rootAlert
  const shouldShowRootAlert = currentSupportUser?.username === 'root';
  useEffect(() => {
    if (shouldShowRootAlert) {
      dispatch(openAlert('userRootWarning'));
    }
  }, [dispatch, shouldShowRootAlert]);

  const shouldShowOverlay = Boolean(!isClientIsOnline || shouldDisplayWarning);

  //AccessExpirationAlert
  const shouldShowAccessExpirationAlert = isNearExpiration;
  useEffect(() => {
    if (shouldShowAccessExpirationAlert) {
      dispatch(openAlert('accessExpirationWarning'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowAccessExpirationAlert]);

  return {
    alerts,
    scopes,
    shouldShowOverlay,
  };
}
